import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Scrolling, SearchPanel, Selection, Editing } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import Form, { SimpleItem } from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import competitionEntityApi from '../../../api/competitionEntityApi';
import { SplitPanel } from '../../utils/split-panel/SplitPanel';
import classNames from 'classnames';

const RelaysLeftPane = React.memo(React.forwardRef(function RelaysLeftPane({ rows, onSelect, onSaving, isSaving, clubLookup, canEdit }, ref) {
    const gridRef = useRef(null);
    React.useImperativeHandle(ref, () => ({
        clearSelection: () => gridRef.current?.instance?.clearSelection(),
    }), []);
    return (
        <div className='dx-card' style={{ padding: 12, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
            <DataGrid
                ref={gridRef}
                className='grid theme-dependent'
                dataSource={rows}
                keyExpr='athleteId'
                columnAutoWidth
                rowAlternationEnabled
                hoverStateEnabled
                showBorders={false}
                repaintChangesOnly
                onSelectionChanged={(e) => { const row = e.selectedRowsData?.[0]; onSelect(row ? row.athleteId : null); }}
                onSaving={onSaving}
                loadPanel={{ enabled: isSaving, text: 'Speichern...' }}
                style={{ flex: 1, minHeight: 0 }}
            >
                <SearchPanel visible placeholder='Suche nach Name, Verein...' />
                <Selection mode='single' />
                <Column dataField='name' caption='Name' />
                <Column dataField='clubId' caption='Verein' lookup={{ dataSource: clubLookup, valueExpr: 'id', displayExpr: 'name' }} />
                <Column dataField='startNumber' caption='Start-Nr.' width={90} />
                <Editing mode='popup' allowAdding={canEdit} useIcons />
                <Scrolling mode='virtual' />
            </DataGrid>
        </div>
    );
}));

export function CompetitionRelays({ competitionId, data, clubs, results, heats, events, accessToken, onEntityChange }) {
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const dataRef = useRef(data);
    useEffect(() => { dataRef.current = data; }, [data]);

    const canEdit = !!accessToken;

    const clubLookup = useMemo(() => (
        clubs?.map((club) => ({ id: club.clubId, name: club.shortname || club.name })) || []
    ), [clubs]);

    const rows = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data.filter(a => a.isRelay === true);
    }, [data]);

    useEffect(() => {
        if (!selectedId) { setFormData(null); setIsDirty(false); return; }
        const item = dataRef.current?.find(a => a.athleteId === selectedId && a.isRelay === true);
        setFormData(item ? { ...item } : null);
        setIsDirty(false);
        setIsEditing(false);
    }, [selectedId]);

    const handleSelect = useCallback((id) => setSelectedId(id), []);
    const leftPaneRef = useRef(null);

    const updateField = useCallback((field, value) => {
        setFormData(prev => prev ? { ...prev, [field]: value } : prev);
        setIsDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
        if (!formData || !accessToken || !selectedId) return;
        setIsSaving(true);
        try {
            const current = dataRef.current?.find(a => a.athleteId === selectedId) || {};
            const saved = await competitionEntityApi.athletes.update(competitionId, selectedId, { ...current, ...formData, athleteId: selectedId, competitionId, isRelay: true }, accessToken);
            onEntityChange?.('Athlete', 'update', saved);
            setIsDirty(false);
            setIsEditing(false);
            notify('Gespeichert', 'success', 2000);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, formData, onEntityChange, selectedId]);

    const handleCancel = useCallback(() => {
        const item = dataRef.current?.find(a => a.athleteId === selectedId);
        setFormData(item ? { ...item } : null);
        setIsDirty(false);
        setIsEditing(false);
    }, [selectedId]);

    const handleDelete = useCallback(async () => {
        if (!accessToken || !selectedId) return;
        if (!window.confirm('Staffel wirklich löschen?')) return;
        setIsSaving(true);
        try {
            await competitionEntityApi.athletes.remove(competitionId, selectedId, accessToken);
            onEntityChange?.('Athlete', 'delete', { athleteId: selectedId });
            setSelectedId(null);
            leftPaneRef.current?.clearSelection();
            notify('Gelöscht', 'success', 2000);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Löschen', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, onEntityChange, selectedId]);

    const handleGridSaving = useCallback(async (e) => {
        if (!e.changes?.length) return;
        if (!accessToken) { notify('Kein Zugriffstoken verfügbar.', 'warning', 3000); e.cancel = true; return; }
        e.cancel = true;
        setIsSaving(true);
        try {
            for (const change of e.changes) {
                if (change.type === 'insert') {
                    const payload = { ...change.data, competitionId, isRelay: true };
                    const saved = await competitionEntityApi.athletes.insert(competitionId, payload, accessToken);
                    onEntityChange?.('Athlete', 'insert', saved);
                }
            }
            e.component.cancelEditData();
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, onEntityChange]);

    const relayResults = useMemo(() => {
        if (!selectedId || !results) return [];
        const heatMap = (heats || []).reduce((m, h) => { m[h.heatId] = h; return m; }, {});
        const eventMap = (events || []).reduce((m, ev) => { m[ev.eventId] = ev; return m; }, {});
        return (results || []).filter(r => r.athleteId === selectedId).map(r => {
            const heat = heatMap[r.heatId];
            const event = heat ? eventMap[heat.eventId] : null;
            return { ...r, eventName: event ? `WK ${event.number} – ${event.name || event.distance + 'm ' + event.stroke}` : 'Unbekannt', heatNumber: heat?.number || '?' };
        });
    }, [selectedId, results, heats, events]);

    const renderFormToolbar = useCallback((title) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
            {canEdit && (
                <div style={{ display: 'flex', gap: 6 }}>
                    {isEditing ? (
                        <>
                            <Button text='Speichern' type='default' stylingMode='contained' disabled={isSaving} onClick={handleSave} />
                            <Button text='Abbrechen' stylingMode='outlined' disabled={isSaving} onClick={handleCancel} />
                            <Button icon='trash' stylingMode='outlined' hint='Löschen' disabled={isSaving} onClick={handleDelete} />
                        </>
                    ) : (
                        <>
                            <Button text='Bearbeiten' icon='edit' stylingMode='outlined' disabled={!formData} onClick={() => setIsEditing(true)} />
                            <Button icon='trash' stylingMode='outlined' hint='Löschen' disabled={!formData || isSaving} onClick={handleDelete} />
                        </>
                    )}
                </div>
            )}
        </div>
    ), [canEdit, formData, handleCancel, handleDelete, handleSave, isEditing, isSaving]);

    const renderDetail = () => {
        if (!formData) {
            return (
                <div className='dx-card' style={{ padding: 20, color: '#888' }}>
                    Bitte eine Staffel aus der Liste links auswählen.
                </div>
            );
        }
        const f = formData;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className={classNames('dx-card', 'plain-styled-form', { 'view-mode': !isEditing })} style={{ padding: 16 }}>
                    {renderFormToolbar(f.name || 'Staffel')}
                    <Form formData={f} labelMode='floating' colCount={4} readOnly={!isEditing} onFieldDataChanged={(e) => updateField(e.dataField, e.value)}>
                        <SimpleItem dataField='name' label={{ text: 'Name' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='firstname' label={{ text: 'Zusatz' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='clubId' label={{ text: 'Verein' }} editorType='dxSelectBox' editorOptions={{ dataSource: clubLookup, valueExpr: 'id', displayExpr: 'name', stylingMode: 'filled' }} />
                        <SimpleItem dataField='startNumber' label={{ text: 'Startnummer' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    </Form>
                </div>
                <div className='dx-card' style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Ergebnisse</div>
                    <DataGrid
                        dataSource={relayResults}
                        keyExpr='resultId'
                        columnAutoWidth
                        showBorders={false}
                        hoverStateEnabled
                        rowAlternationEnabled
                        noDataText='Keine Ergebnisse vorhanden'
                    >
                        <Column dataField='eventName' caption='Wettkampf' />
                        <Column dataField='heatNumber' caption='Lauf' width={70} />
                        <Column dataField='lane' caption='Bahn' width={70} />
                        <Column dataField='rank' caption='Platz' width={70} />
                        <Column dataField='swimtime' caption='Zeit' width={110} />
                        <Column dataField='status' caption='Status' width={110} />
                        <Scrolling mode='virtual' />
                    </DataGrid>
                </div>
            </div>
        );
    };

    return (
        <div className='competition-section'>
            <div className='section-header'>
                <div>
                    <div className='section-title'>Staffeln</div>
                    <div className='section-subtitle'>Gemeldete Staffeln</div>
                </div>
                <div className='section-pill subtle'>{rows.length} Staffel{rows.length !== 1 ? 'n' : ''}</div>
            </div>

            <SplitPanel
                storageKey='splitPanel.relays'
                defaultLeft={320}
                minLeft={160}
                minRight={200}
                left={<RelaysLeftPane ref={leftPaneRef} rows={rows} onSelect={handleSelect} onSaving={handleGridSaving} isSaving={isSaving} clubLookup={clubLookup} canEdit={canEdit} />}
                right={renderDetail()}
            />
        </div>
    );
}
